import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
  Lightning, ChatCircle, IdentificationCard, User,
  Wrench, ChartBar, Desktop, Shuffle,
  List, SignOut, ArrowLeft,
} from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const NAV_OPERATE = [
  { to: "/hermes/chat",     label: "Chat",      icon: ChatCircle,        sub: "Talk to Hermes" },
];

const NAV_BEHAVIOR = [
  { to: "/hermes/identity", label: "Identity",  icon: IdentificationCard,sub: "SOUL.md — mission & rules" },
  { to: "/hermes/operator", label: "Operator",  icon: User,              sub: "USER.md — your preferences" },
];

const NAV_COMING = [
  { label: "Skills",        icon: Wrench,   sub: "Behavioral modules" },
  { label: "Activity Log",  icon: ChartBar, sub: "Cycle history" },
  { label: "Tool Permissions", icon: Desktop, sub: "Access controls" },
  { label: "Workspaces",    icon: Shuffle,  sub: "Persona routing" },
];

function NavItem({ item }) {
  const location = useLocation();
  const Icon = item.icon;
  const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + "/");
  return (
    <NavLink
      to={item.to}
      className={() =>
        `relative flex items-center gap-2.5 px-2.5 py-2 rounded-md transition-all duration-100 ${
          isActive
            ? "bg-white/[0.1] text-white"
            : "text-slate-300 hover:bg-white/[0.06] hover:text-white"
        }`
      }
    >
      {isActive && (
        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-indigo-400 rounded-r-full" />
      )}
      <Icon size={15} weight={isActive ? "fill" : "regular"}
        className={`shrink-0 ${isActive ? "text-white" : "text-slate-400"}`} />
      <div className="min-w-0 flex-1">
        <div className={`text-[13px] font-medium leading-none truncate ${isActive ? "text-white" : "text-slate-300"}`}>
          {item.label}
        </div>
        <div className={`text-[11px] mt-0.5 leading-none truncate ${isActive ? "text-slate-400" : "text-slate-500"}`}>
          {item.sub}
        </div>
      </div>
    </NavLink>
  );
}

function ComingSoonItem({ item }) {
  const Icon = item.icon;
  return (
    <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-md opacity-35 cursor-not-allowed">
      <Icon size={15} className="text-slate-500 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-medium text-slate-400 leading-none truncate">{item.label}</div>
        <div className="text-[11px] mt-0.5 text-slate-600 leading-none truncate">{item.sub}</div>
      </div>
    </div>
  );
}

function Sidebar() {
  return (
    <div className="flex flex-col h-full bg-slate-950">
      {/* Back to hub */}
      <Link
        to="/"
        className="flex items-center gap-2 px-4 py-3 border-b border-slate-800 text-slate-500 hover:text-slate-300 transition-colors shrink-0"
      >
        <ArrowLeft size={13} />
        <span className="text-[11px] font-medium">Back to Hub</span>
      </Link>

      {/* Logo */}
      <div className="flex items-center gap-2.5 px-4 py-4 border-b border-slate-800 shrink-0">
        <div className="w-6 h-6 rounded bg-indigo-600/30 border border-indigo-500/30 flex items-center justify-center shrink-0">
          <Lightning weight="fill" className="text-indigo-400" size={12} />
        </div>
        <div>
          <div className="font-display text-[13px] font-bold text-white leading-none tracking-tight">
            Control Center
          </div>
          <div className="text-[10px] text-slate-400 mt-0.5 leading-none tracking-wider uppercase">
            Hermes Governance
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        <div>
          <div className="px-2.5 mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
            Operate
          </div>
          <div className="space-y-0.5">
            {NAV_OPERATE.map((item) => <NavItem key={item.to} item={item} />)}
          </div>
        </div>
        <div>
          <div className="px-2.5 mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
            Behavior
          </div>
          <div className="space-y-0.5">
            {NAV_BEHAVIOR.map((item) => <NavItem key={item.to} item={item} />)}
          </div>
        </div>
        <div>
          <div className="px-2.5 mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
            Coming Soon
          </div>
          <div className="space-y-0.5">
            {NAV_COMING.map((item) => <ComingSoonItem key={item.label} item={item} />)}
          </div>
        </div>
      </nav>
    </div>
  );
}

export default function HermesLayout({ children }) {
  const { user, logout } = useAuth();
  const location  = useLocation();
  const [mob, setMob] = useState(false);

  const allNav = [...NAV_OPERATE, ...NAV_BEHAVIOR];
  const current = allNav.find((n) =>
    location.pathname === n.to || location.pathname.startsWith(n.to + "/")
  );

  const initials = (user?.name || user?.email || "?")
    .split(/[\s@.]/).filter(Boolean).slice(0, 2)
    .map((p) => p[0]?.toUpperCase()).join("");

  return (
    <div className="min-h-screen flex bg-white">
      {/* Sidebar */}
      <aside className="hidden md:flex w-56 shrink-0 flex-col bg-slate-950 border-r border-slate-800">
        <Sidebar />
      </aside>

      {/* Mobile */}
      {mob && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          <div className="absolute inset-0 bg-slate-900/60" onClick={() => setMob(false)} />
          <aside className="relative z-10 w-56 bg-slate-950 border-r border-slate-800 flex flex-col">
            <Sidebar />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
          <div className="flex items-center justify-between px-5 h-11">
            <div className="flex items-center gap-3">
              <button className="md:hidden w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100"
                onClick={() => setMob(true)}>
                <List size={16} />
              </button>
              {current && (
                <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
                  {current.label}
                </span>
              )}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 transition-colors">
                  <div className="w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-bold flex items-center justify-center">
                    {initials || "U"}
                  </div>
                  <span className="hidden sm:block text-[13px] text-slate-600 font-medium truncate max-w-[120px]">
                    {user?.name || user?.email}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>
                  <div className="text-[10px] text-slate-400 font-normal uppercase tracking-wider">Signed in as</div>
                  <div className="text-xs font-medium text-slate-900 truncate mt-0.5">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="cursor-pointer text-xs">
                  <SignOut size={13} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 min-h-0">{children}</main>
      </div>
    </div>
  );
}
